// Realtime WS event catalog (WP-124; PRD §10.11; structure doc §6.6).
//
// This is the *second* catalog in this package. `catalog.ts`'s `EventCatalog` is the outbox's
// catalog — durable rows a module writes inside its own transaction. `WsEventCatalog` below is
// the WebSocket gateway's catalog: every event name the dashboard (and later the mobile PWA)
// may receive over `/v1/realtime`, with its payload shape. The two catalogs overlap
// (`channel.qr.disconnected` is written to the outbox *and* fanned out over WS — see
// `apps/workers/src/queues/outbox-relay.ts`), but they are not the same list: most `EventCatalog`
// rows never reach a browser, and most of `WsEventCatalog` (`message.new`, `typing`, ...) has no
// producer yet — this sprint defines names and payload shapes only (no `Message`/`Conversation`
// module exists before WP-118). `WsEventCatalog` is the single source of truth for WS event
// names: **no event name may be hand-typed as a string literal anywhere else** (gateway,
// publisher or dashboard client) — always go through `WsEventName`/`isWsEventName`.
export interface WsEventCatalog {
  /** WP-315: wallet balance changed (same payload as `EventCatalog["wallet.updated"]`). */
  "wallet.updated": {
    readonly walletId: string;
    readonly balanceMinor: string;
    readonly currency: string;
    readonly at: string;
  };
  /** WP-118 (not yet emitted this sprint): a new inbound/outbound message landed. */
  "message.new": {
    readonly conversationId: string;
    readonly messageId: string;
    readonly direction: "inbound" | "outbound";
    readonly preview: string;
    readonly at: string;
  };
  /** WP-118 (not yet emitted this sprint): a message's delivery status changed. */
  "message.status": {
    readonly conversationId: string;
    readonly messageId: string;
    readonly status: "queued" | "sent" | "delivered" | "read" | "failed";
    readonly at: string;
  };
  /**
   * WP-221: a conversation changed assignee/team (relayed from the outbox row written in the
   * same transaction). Ids only; clients refetch through the scope-filtered REST API.
   * `previousAssigneeUserId` lets the former assignee drop the thread from "mine" at once.
   */
  "conversation.assigned": {
    readonly conversationId: string;
    readonly assigneeUserId: string | null;
    readonly teamId?: string | null;
    readonly previousAssigneeUserId?: string | null;
    readonly at: string;
  };
  /** Sprint 3+ inbox (not yet emitted this sprint): status/tags/other conversation metadata
   * changed. */
  "conversation.updated": {
    readonly conversationId: string;
    /** WP-219: current unread count (a number only; no content, see `message.created`). */
    readonly unreadCount?: number;
    readonly status?: string;
    readonly at: string;
  };
  /** WP-219: a message was persisted; clients refetch the thread / list (scope-filtered). */
  "message.created": {
    readonly conversationId: string;
    readonly messageId: string;
    readonly direction: "inbound" | "outbound";
    readonly at: string;
  };
  /** WP-304/305: a cart changed (same payload as `EventCatalog["cart.updated"]`). */
  "cart.updated": {
    readonly workspaceId: string;
    readonly cartId: string;
    readonly contactId: string;
    readonly changeType:
      | "item_added"
      | "item_updated"
      | "item_removed"
      | "item_saved"
      | "item_restored"
      | "discount_changed"
      | "approval_changed"
      | "sent"
      | "notes_changed"
      | "coupon_changed";
    readonly item?: {
      readonly id: string;
      readonly lineType: "product" | "service" | "custom";
      readonly productCacheId: string | null;
      readonly qty: number;
      readonly unitPriceMinor: string;
      readonly discountMinor: string;
      readonly currency: string;
    };
    readonly at: string;
  };
  /** WP-305: a cart discount was approved/rejected (same payload as the outbox event). */
  "cart.approval.decided": {
    readonly workspaceId: string;
    readonly cartId: string;
    readonly approvalId: string;
    readonly decision: "approved" | "rejected";
    /** The requesting agent — the one to notify. */
    readonly requestedByUserId: string;
    readonly decidedByUserId: string;
    readonly note: string | null;
    readonly at: string;
  };
  /** commerce-kit (not yet emitted this sprint): a quote drafted for a conversation changed. */
  "quote.updated": {
    readonly conversationId: string;
    readonly quoteId: string;
    readonly status: string;
    readonly at: string;
  };
  /** Sprint 3+ inbox (not yet emitted this sprint): a human/AI agent started or stopped typing. */
  typing: {
    readonly conversationId: string;
    readonly userId: string | null;
    readonly typing: boolean;
  };
  /**
   * WP-117 Cloud API (not yet emitted this sprint): the shared shape for a Cloud API channel
   * disconnect. Named separately from `channel.qr.disconnected` because Cloud vs QR channels
   * surface very different remediation UI.
   */
  "channel.disconnected": {
    readonly channelId: string;
    readonly reason: string;
    readonly at: string;
  };
  /**
   * WP-120/WP-122: the first real end-to-end event (build step 8) — written to the outbox by
   * `apps/waha-orchestrator`'s poll loop and fanned out here unchanged. Payload shape matches
   * `EventCatalog["channel.qr.disconnected"]` (`../catalog.ts`) exactly; see
   * `toWsEnvelope` (`./ws-fanout.ts`) for the mapping.
   */
  "channel.qr.disconnected": {
    readonly channelId: string;
    readonly sessionName: string;
    readonly previousStatus: string;
    readonly currentStatus: string;
    readonly at: string;
  };
  /**
   * WP-203: active editors of a flow (soft-lock avatars). Full replacement list per event; an
   * empty list means nobody is editing. Broadcast on the per-workspace channel only.
   */
  "flow.presence.update": {
    readonly flowId: string;
    readonly editors: readonly {
      readonly userId: string;
      readonly displayName: string;
      readonly cursor?: { readonly x: number; readonly y: number };
    }[];
  };
  /**
   * WP-252: per-node counters of a flow version changed. Throttled (<= 1 per 2s per flow); the
   * payload only names the version, clients refetch `.../versions/:id/counters`.
   */
  "flow.counters.update": {
    readonly flowId: string;
    readonly flowVersionId: string;
  };
  /**
   * WP-221B: agents viewing / typing in a conversation. Full replacement viewer list per event
   * (empty = nobody). Carries user ids only; broadcast on the per-workspace channel.
   */
  "conversation.presence.update": {
    readonly conversationId: string;
    readonly viewers: readonly { readonly userId: string; readonly typing: boolean }[];
  };
  /**
   * WP-620: a supervisor whisper. Published ONLY to the whisper-scoped topic
   * (the `topics.ts` helper in `@wato/realtime-kit`), never to the workspace or conversation
   * channels.
   */
  "conversation.whisper": {
    readonly conversationId: string;
    readonly whisperId: string;
    readonly fromManagerId: string;
    readonly toAgentId: string;
    readonly text: string;
    readonly at: string;
  };
}

export type WsEventName = keyof WsEventCatalog;

/** The full, ordered list of WS event names — iterate it instead of `Object.keys` on the type. */
export const WS_EVENT_NAMES: readonly WsEventName[] = [
  "wallet.updated",
  "message.new",
  "message.status",
  "conversation.assigned",
  "conversation.updated",
  "message.created",
  "cart.updated",
  "cart.approval.decided",
  "quote.updated",
  "typing",
  "channel.disconnected",
  "channel.qr.disconnected",
  "flow.presence.update",
  "flow.counters.update",
  "conversation.presence.update",
  "conversation.whisper",
];

const WS_EVENT_NAME_SET: ReadonlySet<string> = new Set(WS_EVENT_NAMES);

/** Narrows an arbitrary string (e.g. an outbox row's `event_name`) to a `WsEventName`. */
export function isWsEventName(value: string): value is WsEventName {
  return WS_EVENT_NAME_SET.has(value);
}

/**
 * The envelope every WS message carries (build step 6/7). `conversationId` is present only for
 * conversation-scoped events; workspace-only events (`channel.*`) omit it.
 */
export interface WsEnvelope<N extends WsEventName = WsEventName> {
  readonly event: N;
  readonly workspaceId: string;
  readonly conversationId?: string;
  readonly payload: WsEventCatalog[N];
  /** ISO-8601 emit time (not the underlying business event's `at`, though they often match). */
  readonly emittedAt: string;
}

/** Builds a typed envelope; the payload is checked against `WsEventCatalog`. */
export function createWsEnvelope<N extends WsEventName>(
  event: N,
  workspaceId: string,
  payload: WsEventCatalog[N],
  options: { readonly conversationId?: string; readonly emittedAt?: string } = {},
): WsEnvelope<N> {
  return {
    event,
    workspaceId,
    payload,
    emittedAt: options.emittedAt ?? new Date().toISOString(),
    ...(options.conversationId === undefined ? {} : { conversationId: options.conversationId }),
  };
}

/** Redis Pub/Sub channel a workspace's gateway subscribers join (build step 2; PRD §10.11). */
export function workspaceChannel(workspaceId: string): string {
  return `ws:workspace:${workspaceId}`;
}

/** Redis Pub/Sub channel for one conversation, joined in addition to the workspace channel when
 * a client subscribes with a `conversationId` (build step 7). */
export function conversationChannel(workspaceId: string, conversationId: string): string {
  return `ws:workspace:${workspaceId}:conversation:${conversationId}`;
}
